import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, forkJoin, map, mergeMap, Observable } from 'rxjs';
import { HttpApiService } from '../../api/audit-logs/services';
import {
  ApiBoardSettingsResponse,
  ApiCreateMonetaryValueRequest,
  ApiCreateValueRequest,
  ApiUpdateMonetaryValueRequest,
  ApiUpdateValueRequest,
  ApiValueResponse,
} from '../../api/core/models';
import { CreateValueRequest } from '../../api/core/models/api/create-value-request';
import { ListCommentsResponse } from '../../api/core/models/api/list-comments-response';
import { ListInventoriesResponse } from '../../api/core/models/api/list-inventories-response';
import { MonetaryValueResponse } from '../../api/core/models/api/monetary-value-response';
import { UpdateValueRequest } from '../../api/core/models/api/update-value-request';
import { UpdateValuesRequest } from '../../api/core/models/api/update-values-request';
import { BoardSettingsService, InventoryService } from '../../api/core/services';
import { EstimatesService } from '../../api/data-definitions/services';
import { toClientVariables } from '../../functions/model/to-client-variables';
import { ClientBoard } from '../../model/client-model/client-board';
import { ClientParameterDetails } from '../../model/client-model/client-parameter';
import { ClientVariable } from '../../model/client-model/client-variable';
import { selectMonetaryVariableIds } from '../../store/inventories/inventories.selectors';
import { InventoriesWithValues } from '../../store/inventories/inventories.types';
import { apiBoardSettingsToMeasurementSystem } from '../backend-service/helpers';
import { IClimateCortexModelService } from '../climate-engine/ccc-algorithm/model-service';
import { InventoryUpdate } from '../store/inventories/inventory-save-manager.service';
import { UnitConversionService } from '../unit-conversion/unit-conversion.service';
import { apiAuditLogsAndCommentsToClientParameterDetails } from './backend-inventory.service.helpers';

@Injectable({
  providedIn: 'root',
})
export class BackendInventoryService {
  private _boardSettingsService = inject(BoardSettingsService);
  private _cccModelService = inject(IClimateCortexModelService);
  private _estimatesService = inject(EstimatesService);
  private _httpApiService = inject(HttpApiService);
  private _inventoryService = inject(InventoryService);
  private _store = inject(Store);
  private _unitConversionService = inject(UnitConversionService);

  createInventoryVariableMonetaryValue({
    boardId,
    inventoryId,
    variable,
    currency,
  }: {
    boardId: ClientBoard['id'];
    inventoryId: string;
    currency: NonNullable<ClientBoard['boardSettings']['currency']>;
    variable: ClientVariable;
  }): Observable<MonetaryValueResponse> {
    const id = inventoryId;
    const value = <number>variable.metricValue;
    const variableDefinitionId = <string>variable.name;
    const valueOnDate = new Date().toISOString();
    const body: ApiCreateMonetaryValueRequest = { value, valueOnDate, variableDefinitionId, currency };

    return this._inventoryService.createInventoryMonetaryValue({ boardId, id, body });
  }

  createInventoryVariableValue({
    boardId,
    inventoryId,
    variable,
    valueMetaData,
  }: {
    boardId: ClientBoard['id'];
    inventoryId: string;
    variable: ClientVariable;
    valueMetaData?: CreateValueRequest['changeReason'];
  }): Observable<ApiValueResponse> {
    const id = inventoryId;
    const variableDefinitionId = <string>variable.name;
    const value = <number>variable.metricValue;
    const body: ApiCreateValueRequest = {
      value,
      variableDefinitionId,
      ...(valueMetaData ? { changeReason: valueMetaData } : {}),
    };

    return this._inventoryService.createInventoryValue({ boardId, id, body });
  }

  createMonetaryValueComment({
    boardId,
    inventoryId,
    valueId,
    comment,
  }: {
    boardId: ClientBoard['id'];
    inventoryId: string;
    valueId: NonNullable<ClientVariable['valueId']>;
    comment: string;
  }): Observable<string> {
    const body = { comment };
    return this._inventoryService.createInventoryMonetaryValueComment({
      boardId,
      inventoryId,
      monetaryValueId: valueId,
      body,
    });
  }

  createValueComment({
    boardId,
    inventoryId,
    valueId,
    comment,
  }: {
    boardId: ClientBoard['id'];
    inventoryId: string;
    valueId: NonNullable<ClientVariable['valueId']>;
    comment: string;
  }): Observable<string> {
    const body = { comment };
    return this._inventoryService.createInventoryValueComment({ boardId, inventoryId, valueId, body });
  }

  getInventories(
    boardId: ClientBoard['id'],
    baselineYear: number,
    estimatesLocale: string | undefined
  ): Observable<InventoriesWithValues | null> {
    const getInventories = this.listInventories(boardId).pipe(map(({ inventories }) => inventories ?? []));
    const getEstimates = this._estimatesService
      .listEstimates({ estimatesLocaleId: estimatesLocale })
      .pipe(map(({ estimates }) => estimates ?? []));
    const getMeasurementSystem = this._boardSettingsService
      .getBoardSettings$Response({ boardId })
      .pipe(map(({ body }) => apiBoardSettingsToMeasurementSystem(body as ApiBoardSettingsResponse)));

    return forkJoin([getInventories, getEstimates, getMeasurementSystem]).pipe(
      map(([inventories, estimates, measurementSystem]) => {
        if (!inventories) {
          return null;
        }
        const inventoriesWithValues: InventoriesWithValues = new Map();
        inventories.forEach((inventory) => {
          const allVariableValues = new Map();
          if (inventory.values) {
            inventory.values.forEach((value) => {
              allVariableValues.set(value.variableDefinitionId, value);
            });
          }
          if (inventory.monetaryValues) {
            inventory.monetaryValues.forEach((monetaryValue) => {
              allVariableValues.set(monetaryValue.variableDefinitionId, monetaryValue);
            });
          }
          const variables = toClientVariables({
            variableDefinitions: this._cccModelService.variables,
            variableValues: allVariableValues,
            variableEstimates: estimates,
            measurementSystem,
            unitConversionService: this._unitConversionService,
          });
          inventoriesWithValues.set(inventory.id, {
            id: inventory.id,
            inventoryYear: inventory.year,
            variables,
            isBaselineYear: baselineYear === inventory.year,
          });
        });
        return inventoriesWithValues;
      })
    );
  }

  listInventories(boardId: string): Observable<ListInventoriesResponse> {
    return this._inventoryService.listInventories$Response({ boardId }).pipe(
      map(({ body: { inventories }, status }) => {
        if (status === 202) {
          // Not an error but practically we treat it as one where we want to prompt the user to refresh the page
          throw new HttpErrorResponse({ error: new Error(), url: this._inventoryService.rootUrl, status: 202 });
        }
        return { inventories };
      })
    );
  }

  updateInventoryVariableMonetaryValue({
    boardId,
    currency,
    inventoryId,
    updatedVariable,
  }: {
    boardId: ClientBoard['id'];
    currency: NonNullable<ClientBoard['boardSettings']['currency']>;
    inventoryId: string;
    updatedVariable: ClientVariable;
  }): Observable<string> {
    const id = <string>updatedVariable.valueId;
    const valueOnDate = <string>new Date().toISOString();
    const body: ApiUpdateMonetaryValueRequest = { value: updatedVariable.value, currency, valueOnDate };

    return this._inventoryService.updateInventoryMonetaryValue({ boardId, inventoryId, id, body });
  }

  updateInventoryMonetaryValue({
    id,
    boardId,
    inventoryId,
    updatedMonetaryValue,
  }: {
    id: string;
    boardId: string;
    inventoryId: string;
    updatedMonetaryValue: MonetaryValueResponse;
  }) {
    const valueOnDate = <string>new Date().toISOString();
    const body: ApiUpdateMonetaryValueRequest = {
      ...updatedMonetaryValue,
      valueOnDate,
    };

    return this._inventoryService.updateInventoryMonetaryValue({ boardId, inventoryId, id, body });
  }

  /**
   *
   * @deprecated use
   */
  updateInventoryVariableValue({
    boardId,
    inventoryId,
    updatedVariable,
    valueMetaData,
  }: {
    boardId: ClientBoard['id'];
    inventoryId: string;
    updatedVariable: ClientVariable;
    valueMetaData: UpdateValueRequest['changeReason'];
  }): Observable<string> {
    const id = <string>updatedVariable.valueId;
    const body: ApiUpdateValueRequest = {
      value: <number>updatedVariable.metricValue,
      ...(valueMetaData ? { changeReason: valueMetaData } : {}),
    };

    return this._inventoryService.updateInventoryValue({ boardId, inventoryId, id, body });
  }

  updateInventoryValues({ boardId, updates }: { boardId: string; updates: InventoryUpdate[] }) {
    const values = updates.reduce<{ [key: string]: ApiUpdateValueRequest }>((acc, { valueId, value, origin }) => {
      acc[valueId] = {
        value,
        ...(origin ? { changeReason: { ...origin } } : {}),
      };
      return acc;
    }, {});
    return this._inventoryService.updateValues({ boardId, body: { values } as UpdateValuesRequest });
  }

  getVariableLogs({
    boardId,
    variableValueId,
    inventoryId,
    variableNameId,
  }: {
    boardId: ClientBoard['id'];
    variableValueId: string;
    inventoryId: string;
    variableNameId: string;
  }): Observable<ClientParameterDetails> {
    return this._store.select(selectMonetaryVariableIds).pipe(
      mergeMap((monetaryVariableIds) => {
        const isMonetaryValue = monetaryVariableIds.has(variableNameId);
        let comments$: Observable<ListCommentsResponse>;
        if (isMonetaryValue) {
          comments$ = this._inventoryService.listCommentsByMonetaryValueId({
            boardId,
            inventoryId,
            monetaryValueId: variableValueId,
          });
        } else {
          comments$ = this._inventoryService.listCommentsByValueId({
            boardId,
            inventoryId,
            valueId: variableValueId,
          });
        }
        return combineLatest([
          this._httpApiService.getEntityAuditLogs({
            boardId,
            id: variableValueId,
          }),
          comments$,
        ]);
      }),
      map(([auditLogs, comments]) =>
        apiAuditLogsAndCommentsToClientParameterDetails(auditLogs, comments, variableNameId)
      )
    );
  }

  listCommentsByCccId({ boardId, carbonCausalChainId }: { boardId: string; carbonCausalChainId: string }) {
    return this._inventoryService.listCommentsByCccIdOrVarId({ boardId, carbonCausalChainId });
  }

  listCommentsByVarDefId({ boardId, variableDefinitionId }: { boardId: string; variableDefinitionId: string }) {
    return this._inventoryService.listCommentsByCccIdOrVarId({ boardId, variableDefinitionId });
  }
}
